import { newId } from './id'
import { isDrive, routeState, sameCoord } from './route'
import { CATEGORY_RENAMES } from './seed'
import { fahrzeug, KEIN_FAHRZEUG, ohneFahrzeug } from './vehicle'
import type { DiaryEntry, Expense, LatLon, Stage, Trip } from './types'

/**
 * Eine Reise ohne Inhalt - das, was beim Anlegen entsteht.
 *
 * Das Gegenstück zu SEED_TRIP, und seit es mehrere Reisen gibt der Normalfall:
 * wer "Atlantik 2027" anlegt, bekommt eine leere Reise und nicht die
 * vorgeplante Balkanroute. Die Vorlage gibt es weiterhin, aber nur auf
 * ausdrücklichen Wunsch beim Anlegen (siehe data/beispiel.ts).
 *
 * Sie ist auch der Rückfall überall dort, wo das Reisedokument fehlt: im
 * Provider, bevor der erste Abgleich gelaufen ist, und beim Lesen einer
 * Exportdatei, in der das Feld fehlt. Ehrlich ist dort nur das Leere - eine
 * fremde Reise anzuzeigen, bis der Server antwortet, ist schlimmer als nichts
 * anzuzeigen.
 *
 * Die Besatzung steht auf eins und nicht auf null: eine Reise ohne Menschen
 * gibt es nicht, und die Zahl geht als Teiler in die Auswertung.
 *
 * Nie verändern, nur als Vorlage spreizen. Das gilt für SEED_TRIP genauso;
 * alle Schreibwege gehen über writeTrip und legen dabei neue Objekte an.
 */
export const EMPTY_TRIP: Trip = {
  meta: {
    title: '',
    vehicle: '',
    crew: 1,
    start: '',
    consumption: 0,
    fuelPrice: 0,
    currency: 'EUR',
  },
  /**
   * Ein Fahrzeug ohne Angaben - und ausdrücklich nicht das der ersten Reise.
   *
   * Dass es überhaupt dasteht, ist die Grenze zwischen „hier hat noch niemand
   * etwas eingetragen“ und „hier stammt das Dokument aus der Zeit vor dem
   * Fahrzeug“. Nur an dieser Grenze lässt sich die Überführung festmachen: eine
   * neue Reise fängt ohne Zuschlag und ohne Schild an, eine bestehende behält
   * die 25 Prozent, mit denen sie geplant wurde (siehe ueberfuehreFahrzeug in
   * lib/vehicle.ts).
   */
  vehicle: KEIN_FAHRZEUG,
  stages: [],
}

/** Etappen, die tatsächlich gefahren werden. Ausgelassene zählen nirgends mit. */
export function activeStages(trip: Trip): Stage[] {
  return trip.stages.filter((s) => !s.skip)
}

/** Kennzahlen der Reise, wie sie im Kopf der App stehen. */
export interface TripTotals {
  km: number
  hours: number
  drivenKm: number
  drivenCount: number
  stageCount: number
  /** Geschätzte Kraftstoffkosten für die geplante Strecke. */
  fuelCost: number
}

export function tripTotals(trip: Trip, diary: Record<string, DiaryEntry>): TripTotals {
  const active = activeStages(trip)

  let km = 0
  let hours = 0
  let drivenKm = 0
  let drivenCount = 0

  for (const s of active) {
    km += finite(s.km)
    hours += finite(s.h)

    const entry = diary[s.id]
    if (entry?.done) {
      drivenCount++
      // Der tatsächliche Wert schlägt die Planung, sobald er eingetragen ist.
      const real = Number(entry.realKm)
      drivenKm += Number.isFinite(real) && real > 0 ? real : finite(s.km)
    }
  }

  // Der Verbrauch kommt vom Fahrzeug und nicht mehr unmittelbar aus den
  // Rahmendaten: dort steht er nur noch als Kopie für ältere Fassungen der App.
  const fuelCost = (km / 100) * finite(fahrzeug(trip).consumption) * finite(trip.meta.fuelPrice)

  return { km, hours, drivenKm, drivenCount, stageCount: active.length, fuelCost }
}

function finite(v: number): number {
  return Number.isFinite(v) ? v : 0
}

/** Planung und berechnete Strecke im Vergleich. */
export interface RouteComparison {
  /** Etappen mit aktueller Strecke. */
  routed: number
  /** Etappen mit veralteter Strecke. */
  stale: number
  /** Etappen, die eine Strecke bräuchten und keine haben. */
  missing: number
  /** Handwerte km der Etappen mit aktueller Strecke. */
  plannedKm: number
  /** Handwerte h derselben Etappen. */
  plannedHours: number
  /** Was der Dienst für dieselben Etappen gerechnet hat. */
  routedKm: number
  /** Fahrzeit laut Dienst für dieselben Etappen, also ohne Zuschlag. */
  routedHours: number
}

/**
 * Stellt Planung und Berechnung gegenüber.
 *
 * Bewusst getrennt von tripTotals und nicht darin: die Handwerte in km und h
 * bleiben die Grundlage der Reise. Der Nutzer darf sie überschreiben, und im
 * Kopf der App, im Budget und in der Kraftstoffrechnung soll genau der Wert
 * stehen, den er dort eingetragen hat - nicht ein zweiter, der ihm
 * daruntergeschoben wird.
 *
 * Verglichen wird nur über die Etappen mit aktueller Strecke. Alles andere
 * wäre eine Zahl, die aussieht wie ein Vergleich und keiner ist: eine Summe
 * über sechzehn Etappen gegen eine über vier gehalten sagt nichts darüber,
 * wie gut die Planung war, sondern nur, dass zwölf Strecken fehlen.
 *
 * Standtage zählen nicht als fehlend - dort gibt es keine Fahrt.
 */
export function routeComparison(stages: Stage[]): RouteComparison {
  const out: RouteComparison = {
    routed: 0,
    stale: 0,
    missing: 0,
    plannedKm: 0,
    plannedHours: 0,
    routedKm: 0,
    routedHours: 0,
  }

  for (const stage of stages) {
    const state = routeState(stage)
    if (state === 'current' && stage.route) {
      out.routed++
      out.plannedKm += finite(stage.km)
      out.plannedHours += finite(stage.h)
      out.routedKm += finite(stage.route.distanceKm)
      out.routedHours += finite(stage.route.durationH)
      continue
    }
    // Ohne Fahrt gibt es nichts zu berechnen, also fehlt dort auch nichts.
    if (!isDrive(stage)) continue
    if (state === 'stale') out.stale++
    else out.missing++
  }

  return out
}

/** Vergibt die Tagesnummern neu, sodass sie lücken- und sprungfrei sind. */
export function renumber(stages: Stage[]): Stage[] {
  return stages.map((s, i) => (s.day === i + 1 ? s : { ...s, day: i + 1 }))
}

/**
 * Verschiebt eine Etappe um eine Position. Liegt das Ziel ausserhalb der Liste,
 * bleibt die Reihenfolge unverändert.
 */
export function moveStage(stages: Stage[], id: string, direction: -1 | 1): Stage[] {
  const i = stages.findIndex((s) => s.id === id)
  const j = i + direction
  if (i < 0 || j < 0 || j >= stages.length) return stages

  const next = [...stages]
  const a = next[i]!
  const b = next[j]!
  next[i] = b
  next[j] = a
  return renumber(next)
}

/**
 * Wo eine Reise beginnt.
 *
 * Die Angabe gibt es nur an einer einzigen Stelle: bei der allerersten Etappe.
 * Jede weitere übernimmt ihren Start vom Ziel der vorherigen, und deshalb steht
 * hier auch kein Vorgabewert - wer eine Reise anlegt, sagt, wo sie losgeht.
 */
export interface Reisestart {
  /** Der Punkt, als [Breite, Länge]. */
  at: LatLon
  /** Wie der Ort heisst. Leer heisst „Start“. */
  ort: string
}

/**
 * Legt hinter der angegebenen Etappe eine neue an und knüpft sie geografisch an.
 *
 * Der Start kommt vom Ziel der vorherigen Etappe - das ist die Kette, die die
 * Reise zusammenhält. Das Ziel der neuen Etappe sitzt zunächst auf ihrem Start:
 * eine Etappe der Länge null, bis jemand sagt, wohin es geht. Das ist keine
 * Behauptung über eine Fahrt, denn ohne Ziel gibt es keine (siehe isDrive in
 * lib/route.ts), und berechnet wird deshalb auch nichts.
 *
 * Ohne vorherige Etappe gibt es nichts zu übernehmen. Hier stand bis hierher
 * fest verdrahtet Bern - der Startpunkt der ersten Reise, für die diese App
 * gebaut wurde. Solange jede neue Reise mit deren sechzehn Etappen begann, fiel
 * das niemandem auf; seit eine Reise leer angelegt werden kann, begann jede
 * zweite Reise stillschweigend an einem Ort, den niemand gewählt hat: Basel -
 * Münchenbuchsee sind hundert Kilometer und wurden als zehn geplant, weil
 * gerechnet wurde, wie weit es von Bern ist.
 *
 * Deshalb wird hier nichts mehr erfunden. Ohne vorherige Etappe braucht die
 * neue einen Start, den jemand gewählt hat - und ohne den entsteht sie nicht.
 * Die Oberfläche fragt danach, bevor sie den Knopf anbietet
 * (features/stages/ErsteEtappe.tsx).
 *
 * Der mitgegebene Start gilt nur in einer leeren Reise. Er beantwortet die
 * Frage „wo beginnt die Reise?“, und die gibt es genau einmal: sobald eine
 * Etappe dasteht, ist sie beantwortet. Eine Etappe, die auf eine unbekannte
 * Kennung hin ans Ende einer bestehenden Liste fiele, begänne an diesem Start
 * statt am Ziel der letzten Etappe - wieder ein Ort, den an dieser Stelle
 * niemand gewählt hat, nur diesmal am anderen Ende der Reise. Das passiert
 * nicht von Hand, sondern wenn der Abgleich Etappen von einem anderen Gerät
 * bringt, während die Maske für die erste Etappe noch offen ist. Dann ist die
 * Voraussetzung der Maske weggefallen und nicht ihre Angabe gerettet: hier
 * entsteht nichts, und die Oberfläche sagt es (features/stages/StagesView.tsx).
 *
 * @param start Nur bei der ersten Etappe einer leeren Reise gebraucht und nur
 *   dort benutzt: gibt es eine vorherige, gewinnt deren Ziel.
 * @returns null, wenn es weder eine vorherige Etappe noch einen Start gibt,
 *   und ebenso, wenn die Kennung in einer nicht leeren Liste nicht vorkommt.
 *   Eine Etappe ohne Start wäre eine, die irgendwo anfängt; eine hinter einer
 *   Etappe, die es nicht gibt, stände irgendwo.
 */
export function insertStageAfter(
  stages: Stage[],
  id: string,
  start?: Reisestart,
): { stages: Stage[]; created: Stage } | null {
  const i = stages.findIndex((s) => s.id === id)
  const prev = i >= 0 ? stages[i] : undefined
  if (!prev && stages.length > 0) return null
  const at: LatLon | undefined = prev ? [...prev.c2] : start ? [...start.at] : undefined
  if (!at) return null

  // Der Name des Orts ist freiwillig: wer eine Koordinate abtippt, muss nicht
  // auch noch einen Namen dazu erfinden. „Start“ ist dann keine Behauptung
  // über einen Ort, sondern die Stelle in der Reise.
  const eigener = start?.ort.trim() ?? ''

  const created: Stage = {
    id: newId('s'),
    day: 0,
    from: prev ? prev.to : eigener || 'Start',
    to: 'Neues Ziel',
    c1: at,
    c2: [at[0], at[1]],
    km: 0,
    h: 0,
    land: prev?.land ?? '',
    type: 'kurz',
    camping: [],
    highlights: [],
    night: [],
    tip: '',
  }

  const next = [...stages]
  next.splice(i >= 0 ? i + 1 : next.length, 0, created)
  return { stages: renumber(next), created }
}

/** Woher der Start einer Etappe kommt. */
export interface StartHerkunft {
  /**
   * Die Etappe davor: von ihrem Ziel ist der Start übernommen worden.
   *
   * Fehlt sie, ist dies die erste Etappe der Reise - dann gehört der Start ihr
   * selbst, und nur dort bietet die Oberfläche Felder dafür an.
   */
  vorher?: Stage
  /** Ob der gespeicherte Start noch dort liegt, wo die vorherige Etappe endet. */
  passt: boolean
}

/**
 * Sagt, wem der Start einer Etappe gehört und ob die Kette dort noch hält.
 *
 * Entscheidend ist die Stelle in der Liste und nicht, wann eine Etappe angelegt
 * wurde: Etappen lassen sich verschieben und löschen. Wer die erste löscht,
 * dessen zweite ist ab da die erste und bestimmt ihren Start selbst; wer eine
 * andere nach vorn schiebt, rückt die bisher erste wieder in die Kette ein.
 * Beides ergibt sich hier von allein, weil nichts gespeichert wird, was die
 * Reihenfolge bereits sagt.
 *
 * Ausgelassene Etappen zählen mit. Das ist der Unterschied zu activeStages, das
 * für die Summen die Etappen mit skip herausnimmt: aus der Rechnung fällt eine
 * ausgelassene Etappe heraus, aus der Reihenfolge nicht. Sie steht weiter in der
 * Liste, hat ihr Ziel und behält es - würde die Kette sie überspringen,
 * verschöbe ein Haken bei „Auslassen“ stillschweigend den Start der nächsten
 * Etappe und entwertete deren berechnete Strecke. Wer einen Tag auslässt,
 * entscheidet selbst, was das für den nächsten bedeutet.
 *
 * Ein nicht passender Start ist deshalb auch kein Fehler, den diese Funktion
 * beheben dürfte, sondern eine Auskunft. In der Startbestückung gibt es ihn
 * mehrfach und mit Absicht: der Ausflug von Bled an den Bohinjsee endet am See,
 * und die Etappe danach beginnt trotzdem wieder in Bled. Ein stilles
 * Zurechtrücken machte aus dem Ausflug eine Fahrt, die niemand geplant hat. Die
 * Oberfläche zeigt den Unterschied an und bietet an, ihn anzugleichen - drücken
 * muss ein Mensch.
 */
export function startHerkunft(stages: Stage[], id: string): StartHerkunft {
  const i = stages.findIndex((s) => s.id === id)
  const vorher = i > 0 ? stages[i - 1] : undefined
  if (!vorher) return { passt: true }
  return { vorher, passt: sameCoord(vorher.c2, stages[i]?.c1) }
}

/** Entfernt eine Etappe und nummeriert den Rest neu. */
export function removeStage(stages: Stage[], id: string): Stage[] {
  return renumber(stages.filter((s) => s.id !== id))
}

/**
 * Der heutige Name einer Ausgabenkategorie aus diesem Reisedokument.
 *
 * Unbekannte Namen bleiben, wie sie sind: eine Ausgabe aus einer Datei mit einer
 * frei erfundenen Kategorie soll ihren Text behalten und nicht in "Sonstiges"
 * verschwinden.
 *
 * Umbenannt wird ausschliesslich in einer Reise aus der Zeit vor dem Fahrzeug -
 * siehe CATEGORY_RENAMES in lib/seed.ts, wo steht, warum. Deshalb steht hier
 * das Reisedokument und nicht die Kraftstoffart: die Kraftstoffart ist der
 * heutige Zustand, das fehlende Fahrzeugfeld ist die Herkunft. Mit der
 * Kraftstoffart als Frage schrieb ein Griff ins Auswahlfeld sämtliche
 * „Diesel“-Buchungen einer Reise unwiederbringlich auf „Benzin“ um, sobald
 * jemand das Fahrzeug auf einen Benziner stellte - und dasselbe passierte,
 * solange das Reisedokument noch nicht geladen war, denn die leere Reise fährt
 * einen Benziner.
 */
export function currentCategory(name: string, trip: Trip): string {
  if (!ohneFahrzeug(trip)) return name
  return CATEGORY_RENAMES[name] ?? name
}

/**
 * Eine Ausgabe mit heutigem Kategorienamen.
 *
 * Gibt bei nichts zu tun genau dasselbe Objekt zurück. Daran hängt der
 * Aufrufer im Provider: nur wo sich etwas geändert hat, wird geschrieben und
 * abgeglichen - sonst schriebe jedes Laden den ganzen Bestand neu.
 */
export function migrateExpense(expense: Expense, trip: Trip): Expense {
  const category = currentCategory(expense.category, trip)
  return category === expense.category ? expense : { ...expense, category }
}

/** Summe der Ausgaben je Kategorie, absteigend sortiert. */
export function expensesByCategory(expenses: Expense[]): { key: string; value: number }[] {
  const sums = new Map<string, number>()
  for (const e of expenses) {
    const amount = finite(e.amount)
    if (amount === 0) continue
    sums.set(e.category, (sums.get(e.category) ?? 0) + amount)
  }
  return [...sums.entries()]
    .map(([key, value]) => ({ key, value }))
    .sort((a, b) => b.value - a.value)
}

/** Summe aller Ausgaben. */
export function totalSpent(expenses: Expense[]): number {
  return expenses.reduce((sum, e) => sum + finite(e.amount), 0)
}

/** Leerer Tagebucheintrag für eine Etappe. */
export function emptyDiary(stageId: string): DiaryEntry {
  return { stageId, done: false, date: '', odo: '', realKm: '', weather: '', text: '' }
}
